import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import PublicHeader from "@/components/PublicHeader";
import PublicFooter from "@/components/PublicFooter";
import { PlayCircle } from "lucide-react";

function ytId(url = "") {
  const m = url.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|shorts\/))([\w-]{11})/);
  return m ? m[1] : null;
}

export default function VideoBulletinPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);

  useEffect(() => {
    api.get("/news", { params: { has_video: true, limit: 60 } })
      .then((r) => {
        const withVid = r.data.filter((n) => n.video_url);
        setItems(withVid);
        if (withVid.length > 0) setActive(withVid[0]);
      })
      .finally(() => setLoading(false));
  }, []);

  return (
    <>
      <PublicHeader />
      <main className="max-w-7xl mx-auto px-4 py-10" data-testid="video-page">
        <div className="mb-8 flex items-center gap-3">
          <div className="w-11 h-11 rounded-md bg-[#DC2626] text-white flex items-center justify-center">
            <PlayCircle className="w-6 h-6"/>
          </div>
          <div>
            <h1 className="font-devnagri-heading text-3xl sm:text-4xl font-black text-[#0F172A]">वीडियो बुलेटिन</h1>
            <p className="text-sm text-slate-500 mt-1">ताज़ा वीडियो समाचार · कुल {items.length}</p>
          </div>
        </div>

        {loading ? <div className="text-slate-500">लोड हो रहा है…</div> : (
          items.length === 0 ? (
            <div className="text-center py-16 bg-white border border-slate-200 rounded-lg">
              <PlayCircle className="w-14 h-14 text-slate-300 mx-auto mb-3"/>
              <div className="font-bold text-slate-700">अभी कोई वीडियो समाचार नहीं</div>
              <p className="text-sm text-slate-500 mt-1">व्यवस्थापक जल्द ही वीडियो जोड़ेंगे</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Player */}
              <div className="lg:col-span-2">
                {active && (
                  <div className="bg-black rounded-lg overflow-hidden aspect-video" data-testid="active-player">
                    {ytId(active.video_url) ? (
                      <iframe title={active.title} width="100%" height="100%"
                              src={`https://www.youtube.com/embed/${ytId(active.video_url)}?autoplay=0`}
                              frameBorder="0"
                              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                              allowFullScreen/>
                    ) : <video src={active.video_url} controls className="w-full h-full"/>}
                  </div>
                )}
                {active && (
                  <div className="mt-4">
                    <div className="tag-cat text-[10px] mb-1.5">{active.category}</div>
                    <Link to={`/article/${active.slug}`} className="font-devnagri-heading text-2xl font-black text-[#0F172A] news-title">{active.title}</Link>
                    <p className="mt-2 text-slate-600 line-clamp-2">{active.excerpt}</p>
                    <div className="text-xs text-slate-500 mt-2">— {active.author}</div>
                  </div>
                )}
              </div>

              {/* Playlist */}
              <aside className="lg:col-span-1">
                <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold mb-3 pb-2 border-b-2 border-[#0F172A]">प्लेलिस्ट</div>
                <div className="space-y-3 max-h-[70vh] overflow-y-auto pr-2">
                  {items.map((n) => {
                    const vid = ytId(n.video_url);
                    const thumb = vid ? `https://img.youtube.com/vi/${vid}/mqdefault.jpg` : n.image_url;
                    const selected = active && active.id === n.id;
                    return (
                      <button key={n.id} onClick={() => setActive(n)}
                        className={`w-full text-left flex gap-3 p-2 rounded-md transition ${selected ? "bg-[#0F172A] text-white" : "bg-white hover:bg-slate-100 border border-slate-200"}`}
                        data-testid={`video-item-${n.id}`}>
                        <div className="relative w-28 aspect-video rounded overflow-hidden bg-slate-200 shrink-0">
                          {thumb && <img src={thumb} alt="" className="w-full h-full object-cover"/>}
                          <div className="absolute inset-0 flex items-center justify-center bg-black/20">
                            <PlayCircle className={`w-7 h-7 ${selected ? "text-[#EA580C]" : "text-white/90"}`}/>
                          </div>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className={`text-[10px] uppercase tracking-widest font-admin font-bold ${selected ? "text-[#EA580C]" : "text-[#1E3A8A]"}`}>{n.category}</div>
                          <div className={`font-devnagri-heading font-bold text-sm leading-snug line-clamp-3 ${selected ? "text-white" : "text-[#0F172A]"}`}>{n.title}</div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </aside>
            </div>
          )
        )}
      </main>
      <PublicFooter />
    </>
  );
}
